/**
 * FactorDetailPaneMolecule.tsx
 *
 * Right pane component for the status factors master/detail layout.
 * Shows the configuration form for the selected factor.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { Message } from 'primereact/message';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SwitchAtom from '../../atoms/Switch/SwitchAtom';
import FactorTypeSelectorMolecule from '../FactorTypeSelector/FactorTypeSelectorMolecule';
import RelatedSpheresEditorMolecule from '../RelatedSpheresEditor/RelatedSpheresEditorMolecule';
import ArcHealthEditorMolecule, { type ArcSummary, type ArcTemplateSummary } from '../ArcHealthEditor/ArcHealthEditorMolecule';
import ManualFactorEditorMolecule from '../ManualFactorEditor/ManualFactorEditorMolecule';
import FactorSummaryMolecule from '../FactorSummary/FactorSummaryMolecule';
import AtlasSphereIdentityMolecule from '../Atlas/AtlasSphereIdentity/AtlasSphereIdentityMolecule';
import type { LocalFactor, FactorFormState } from '../../../types/statusFactors';
import type { StatusFactorType, FactorMode } from 'types/spherePoints';
import type { PropertyOption } from '../PropertyFilter/PropertyFilterMolecule';
import type { FilterGroupLeaf } from 'types/filters';
import type { PerspectiveOption as PerspectiveFilterOption } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import type { AvailablePerspective } from '../CheckInScheduleEditor/CheckInScheduleEditorMolecule';
import FactorIdentityHeader from './FactorIdentityHeader';
import RollupChangePreview from './RollupChangePreview';
import { buildSimulateCandidate } from './buildSimulateCandidate';
import { countUnsavedChanges } from '../../../hooks/countUnsavedChanges';
import { useRollupSimulation } from './useRollupSimulation';
import { buildRosterOverrides } from './buildRosterOverrides';
import './FactorDetailPaneMolecule.scss';

export interface FactorDetailPaneMoleculeProps {
  /** Currently selected factor (null if none selected) */
  selectedFactor: LocalFactor | null;
  /** Callback when factor data changes */
  onFactorChange: (updates: Partial<FactorFormState>) => void;
  /** Callback when Apply button is clicked */
  onApply: () => void | Promise<void>;
  /** Callback when Delete button is clicked */
  onDelete: () => void;
  /** Callback when Revert button is clicked */
  onRevert: () => void;
  /** Callback when Reactivate button is clicked (deactivated factors only) */
  onReactivate?: () => void | Promise<void>;
  /** Callback when the mode segmented control changes (active / observing / paused) */
  onSetMode?: (mode: FactorMode) => void | Promise<void>;
  /**
   * EAINT-750: callback when the header show-on-cards switch flips. Immediate
   * persist (no Apply required) — the switch mirrors onSetMode's semantics
   * because it sits in the shared header where the Configure Apply button
   * isn't available.
   */
  onSetShowOnCards?: (showOnCards: boolean) => void | Promise<void>;
  /** Available properties for filters (related spheres) */
  availableProperties: PropertyOption[];
  /** Available perspectives for filters (related spheres) */
  availablePerspectives: PerspectiveFilterOption[];
  /** Props for perspective search */
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
  };
  /** The related-spheres filter tree's "Add condition" catalog (see RelatedSpheresEditorMolecule). */
  renderCatalog?: (opts: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => React.ReactNode;
  /** In-scope sphere type labels for the related-spheres tree's catalog (see RelatedSpheresEditorMolecule). */
  scopeTypeLabels?: string[];
  /** Whether user has permission to edit */
  hasEditPermission: boolean;
  /** Whether a save operation is in progress */
  isSaving?: boolean;
  /** Available arcs on the sphere for arc_health 'specific' selection */
  arcsAvailable?: ArcSummary[];
  /** Available arc templates for arc_health 'by_template' selection */
  arcTemplatesAvailable?: ArcTemplateSummary[];
  /** Current view mode — 'summary' (read-first) or 'configure' (existing editor). Default: 'summary' */
  viewMode?: 'summary' | 'configure';
  /** Called when the user requests a view-mode change */
  onSetViewMode?: (mode: 'summary' | 'configure') => void;
  /** Sphere ID — required for FactorSummaryMolecule data fetching */
  sphereId?: string;
  /** True when the sphere hosts a measure — forwarded so the related-spheres editor mounts its upward card. */
  hostsMeasure?: boolean;
  /** Additional CSS class names */
  className?: string;
}

const FactorDetailPaneMolecule: React.FC<FactorDetailPaneMoleculeProps> = ({
  selectedFactor,
  onFactorChange,
  onApply,
  onDelete,
  onRevert,
  onReactivate,
  onSetMode,
  onSetShowOnCards,
  availableProperties,
  availablePerspectives,
  perspectiveSearchProps,
  renderCatalog,
  scopeTypeLabels,
  hasEditPermission,
  isSaving = false,
  arcsAvailable,
  arcTemplatesAvailable,
  viewMode = 'summary',
  onSetViewMode,
  sphereId,
  hostsMeasure,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const [isReactivating, setIsReactivating] = useState(false);
  const [isModeChanging, setIsModeChanging] = useState(false);
  const [isShowOnCardsChanging, setIsShowOnCardsChanging] = useState(false);

  // Map perspective options to the format expected by ManualFactorEditorMolecule
  // Must be before any early returns to satisfy React hooks rules
  const manualEditorPerspectives: AvailablePerspective[] = useMemo(() => {
    return availablePerspectives.map((p) => ({
      systemName: p.systemName,
      displayName: p.label,
    }));
  }, [availablePerspectives]);

  const isDeactivated = selectedFactor?.status === 'deactivated';
  // The preview is for an editor with unsaved rollup changes on a saved factor; an explicit View never simulates (D93).
  const previewWanted =
    !!selectedFactor &&
    selectedFactor.localData.factorType === 'related_spheres' &&
    !selectedFactor.isNew &&
    selectedFactor.isDirty &&
    hasEditPermission &&
    !isDeactivated &&
    viewMode === 'configure';
  const simulation = useRollupSimulation(
    sphereId ?? perspectiveSearchProps?.sphereId,
    selectedFactor?.id,
    previewWanted && selectedFactor ? buildSimulateCandidate(selectedFactor.localData) : null
  );
  // The roster follows unsaved combine settings only for an editor in Configure; a viewer or explicit View sends none (D93, D99).
  const rosterOverrides =
    !!selectedFactor && !selectedFactor.isNew && hasEditPermission && !isDeactivated && viewMode === 'configure'
      ? buildRosterOverrides(selectedFactor.localData, selectedFactor.originalData)
      : undefined;
  const isPaused = selectedFactor?.status === 'paused';
  const currentMode: FactorMode = selectedFactor?.localData.mode ?? 'active';
  const classes = [
    'factor-detail-pane',
    isDeactivated ? 'factor-detail-pane--deactivated' : '',
    isPaused ? 'factor-detail-pane--paused' : '',
    className,
  ].filter(Boolean).join(' ');

  // No factor selected
  if (!selectedFactor) {
    return (
      <div className={`${classes} factor-detail-pane--empty`}>
        <div className="factor-detail-pane__empty-content">
          <i className="pi pi-arrow-left" />
          <p>{t('statusFactors.detail.selectFactor', 'Select a factor from the list to view or edit its configuration.')}</p>
        </div>
      </div>
    );
  }

  const { localData, isNew, isDirty, hasValidationErrors, validationErrors } = selectedFactor;
  const unsavedCount = countUnsavedChanges(localData, selectedFactor.originalData);

  // Handle type selection for new factors
  const handleTypeSelect = (type: StatusFactorType) => {
    onFactorChange({ factorType: type });
  };

  // Get factor type label
  const getFactorTypeLabel = (type: StatusFactorType | null): string => {
    switch (type) {
      case 'manual':
        return t('statusFactors.types.manual.label', 'Manual Check-In');
      case 'related_spheres':
        return t('statusFactors.types.relatedSpheres.label', 'Related Spheres');
      case 'arc_health':
        return t('statusFactors.types.arcHealth.label');
      default:
        return t('statusFactors.types.new.label', 'New Factor');
    }
  };

  // Render the appropriate editor based on factor type
  const renderEditor = () => {
    const editorDisabled = isDeactivated || !hasEditPermission || isSaving;

    if (!localData.factorType) {
      return (
        <FactorTypeSelectorMolecule
          selectedType={localData.factorType}
          onTypeSelect={handleTypeSelect}
          disabled={editorDisabled}
        />
      );
    }

    switch (localData.factorType) {
      case 'related_spheres':
        return (
          <RelatedSpheresEditorMolecule
            formState={localData}
            onChange={onFactorChange}
            availableProperties={availableProperties}
            availablePerspectives={availablePerspectives}
            perspectiveSearchProps={perspectiveSearchProps}
            disabled={editorDisabled}
            renderCatalog={renderCatalog}
            scopeTypeLabels={scopeTypeLabels}
            coverage={selectedFactor.coverage}
            isDirty={isDirty}
            sphereId={sphereId}
            hostsMeasure={hostsMeasure}
            rosterOverrides={rosterOverrides}
          />
        );

      case 'manual':
        return (
          <ManualFactorEditorMolecule
            formState={localData}
            onChange={onFactorChange}
            sphereId={perspectiveSearchProps?.sphereId}
            availablePerspectives={manualEditorPerspectives}
            validationErrors={validationErrors}
            disabled={editorDisabled}
          />
        );

      case 'arc_health':
        return (
          <ArcHealthEditorMolecule
            formState={localData}
            onChange={onFactorChange}
            disabled={editorDisabled}
            arcsAvailable={arcsAvailable}
            arcTemplatesAvailable={arcTemplatesAvailable}
          />
        );

      default:
        return null;
    }
  };

  const handleReactivate = async () => {
    if (!onReactivate) return;
    setIsReactivating(true);
    try {
      await onReactivate();
    } finally {
      setIsReactivating(false);
    }
  };

  const handleSetMode = async (mode: FactorMode) => {
    if (!onSetMode || mode === currentMode) return;
    setIsModeChanging(true);
    try {
      await onSetMode(mode);
    } catch {
      // Error state is surfaced by the hook's setError.
    } finally {
      setIsModeChanging(false);
    }
  };

  // EAINT-750: immediate PATCH on toggle. Mirrors handleSetMode's error/
  // in-flight semantics; the switch is disabled while a change is inflight.
  const handleSetShowOnCards = async (next: boolean) => {
    if (!onSetShowOnCards || next === localData.showOnCards) return;
    setIsShowOnCardsChanging(true);
    try {
      await onSetShowOnCards(next);
    } catch {
      // Error state is surfaced by the hook's setError.
    } finally {
      setIsShowOnCardsChanging(false);
    }
  };

  // A new factor has no summary to go back to: the link reads "Cancel" and routes to `onDelete`, which
  // StatusFactorsTab treats as a discard-with-confirmation for drafts (EAINT-924).
  const handleBack = () => {
    if (isNew) {
      onDelete();
      return;
    }
    logAnalyticsEvent('factor_summary_returned', undefined, {
      factorId: selectedFactor.id,
      factorType: localData.factorType,
      sphereId,
      isDirty,
    });
    onSetViewMode?.('summary');
  };

  const handleConfigureClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    logAnalyticsEvent('factor_configure_opened', undefined, {
      factorId: selectedFactor?.id,
      factorType: selectedFactor?.localData.factorType,
      sphereId,
    });
    onSetViewMode?.('configure');
  };

  // Header title varies based on factor state.
  // EAINT-842 Board 4 parity: in Summary view the headline is just the factor
  // name (the surrounding mode chip + factor-type subtitle carry the rest of
  // the context).
  // EAINT-848 Board 3 parity: Configure view no longer shows an "Editing:
  // {name}" title for existing factors — the editable factor name in the
  // ManualFactorEditor's identity block IS the heading. Only deactivated /
  // new factor headings remain in Configure view; existing factors return
  // null so the header row only carries the mode chip / controls / Apply.
  const getHeaderTitle = () => {
    if (isDeactivated) {
      return t('statusFactors.deactivated.title', {
        name: localData.name || getFactorTypeLabel(localData.factorType),
      });
    }
    if (isNew) {
      return t('statusFactors.detail.addNew', 'Add New Factor');
    }
    if (viewMode === 'summary') {
      return localData.name || getFactorTypeLabel(localData.factorType);
    }
    return null;
  };

  // EAINT-842 Board 4 parity: small-caps eyebrow subtitle under the headline
  // describes the factor type in a sentence rather than a bare label.
  const subtitleKey = localData.factorType
    ? `statusFactors.detail.subtitle.${localData.factorType}`
    : null;
  const subtitle = subtitleKey ? t(subtitleKey) : null;
  const hasSubtitle = subtitle && subtitle !== subtitleKey;

  // Configure and New mount FactorIdentityHeader for every factor type (D100), so the shared grey header
  // only remains for Summary, deactivated factors and the type chooser of a new factor.
  const identityKind =
    localData.factorType === 'manual' || localData.factorType === 'related_spheres' || localData.factorType === 'arc_health'
      ? localData.factorType
      : null;
  const showIdentityHeader = (viewMode === 'configure' || isNew) && !isDeactivated && identityKind !== null;

  return (
    <div className={classes}>
      {/* Header */}
      {!showIdentityHeader && (
      <div className="factor-detail-pane__header">
        <div className="factor-detail-pane__header-content">
          {hasSubtitle && (
            <span
              className="factor-detail-pane__subtitle"
              data-testid="factor-detail-pane-subtitle"
            >
              {subtitle}
            </span>
          )}
          <div className="factor-detail-pane__title-row">
            {(() => {
              const headerTitle = getHeaderTitle();
              return headerTitle ? (
                <h4 className="factor-detail-pane__title">{headerTitle}</h4>
              ) : null;
            })()}
            {/* EAINT-842 Board 1 parity: read-only mode chip inline with the
                factor-name headline in Summary view. The full segmented
                control (Active / Observing / Paused) is Configure-only —
                mode transitions belong in the edit surface. */}
            {viewMode === 'summary' && !isDeactivated && !isNew && localData.factorType && (
              <span
                className={`factor-detail-pane__mode-chip factor-detail-pane__mode-chip--${currentMode}`}
                data-testid="factor-detail-pane-mode-chip"
              >
                <span
                  className="factor-detail-pane__mode-chip-dot"
                  aria-hidden="true"
                />
                {t(`statusFactors.mode.${currentMode}`)}
              </span>
            )}
            {!isDeactivated && isDirty && (
              <span className="factor-detail-pane__dirty-badge">
                {t('statusFactors.detail.unsaved', 'Unsaved')}
              </span>
            )}
          </div>
        </div>
        {/* EAINT-750: mode segmented control + show-on-cards switch cluster
             together as a single right-side unit so the Configure button can
             sit at the far edge of the header (matches Board 4 mockup). The
             switch renders even when onSetMode is absent (e.g. no permission
             to change mode) because show-on-cards is an independent capability. */}
        {!isDeactivated && localData.factorType && (
          <div className="factor-detail-pane__header-controls">
            <div className="factor-detail-pane__show-on-cards" title={t('statusFactors.showOnCards.description')}>
              <SwitchAtom
                inputId={`factor-show-on-cards-${selectedFactor?.id ?? 'new'}`}
                label={t('statusFactors.showOnCards.label')}
                checked={localData.showOnCards}
                disabled={isSaving || !hasEditPermission || isShowOnCardsChanging || !onSetShowOnCards}
                // EAINT-750: prefer immediate persist (onSetShowOnCards) so the
                // shared header switch works from Summary view — no Apply is
                // available there. Falls back to the dirty-form path only when
                // the parent chooses not to wire the setter.
                onChange={(next) =>
                  onSetShowOnCards
                    ? handleSetShowOnCards(next)
                    : onFactorChange({ showOnCards: next })
                }
              />
            </div>
          </div>
        )}
        {!isDeactivated && viewMode === 'summary' && !isNew && localData.factorType && hasEditPermission && (
          <ButtonAtom
            type="button"
            label={t('statusFactors.actions.configure')}
            icon="pi pi-cog"
            data-testid="btn-configure"
            onClick={handleConfigureClick}
            variant="primary"
            size="small"
          />
        )}
      </div>
      )}

      {showIdentityHeader && identityKind && (
        <FactorIdentityHeader
          kind={identityKind}
          name={localData.name}
          description={localData.description}
          weight={localData.weight}
          mode={currentMode}
          status={selectedFactor.currentStatus ?? null}
          showOnCards={localData.showOnCards}
          canEdit={hasEditPermission && !isSaving}
          modeBusy={isModeChanging || isSaving}
          showOnCardsBusy={isShowOnCardsChanging}
          errors={validationErrors}
          onNameChange={(name) => onFactorChange({ name })}
          onDescriptionChange={(description) => onFactorChange({ description })}
          onWeightChange={(weight) => onFactorChange({ weight })}
          onModeChange={(mode) => (isNew ? onFactorChange({ mode }) : handleSetMode(mode))}
          onShowOnCardsChange={(next) =>
            isNew || !onSetShowOnCards ? onFactorChange({ showOnCards: next }) : handleSetShowOnCards(next)
          }
          onDelete={onDelete}
          onBack={isNew || onSetViewMode ? handleBack : undefined}
          backLabel={isNew ? t('common.cancel', 'Cancel') : undefined}
        />
      )}

      {/* Paused banner — the "by" clause renders an identity component so the
          user id resolves to a display name + avatar via globalSphereCache
          instead of the raw GUID. (EAINT-758) */}
      {isPaused && (
        <div className="factor-detail-pane__paused-banner">
          <i className="pi pi-pause-circle factor-detail-pane__paused-banner-icon" />
          <div className="factor-detail-pane__paused-banner-text">
            {selectedFactor.pausedAt ? (
              <span className="factor-detail-pane__paused-banner-line">
                <span>
                  {t('statusFactors.paused.bannerPrefix', {
                    date: new Date(selectedFactor.pausedAt).toLocaleDateString(i18n.language),
                  })}
                </span>
                {selectedFactor.pausedBy ? (
                  <AtlasSphereIdentityMolecule sphereId={selectedFactor.pausedBy} />
                ) : (
                  <span>{t('common.unknown')}</span>
                )}
              </span>
            ) : (
              <span>{t('statusFactors.paused.readOnly')}</span>
            )}
          </div>
        </div>
      )}

      {/* Deactivation banner — see paused banner note above; same identity
          resolution pattern for the "by" clause (EAINT-758). */}
      {isDeactivated && (
        <div className="factor-detail-pane__deactivated-banner">
          <i className="pi pi-ban factor-detail-pane__deactivated-banner-icon" />
          <div className="factor-detail-pane__deactivated-banner-text">
            {selectedFactor.deactivatedAt ? (
              <span className="factor-detail-pane__deactivated-banner-line">
                <span>
                  {t('statusFactors.deactivated.bannerPrefix', {
                    date: new Date(selectedFactor.deactivatedAt).toLocaleDateString(i18n.language),
                  })}
                </span>
                {selectedFactor.deactivatedBy ? (
                  <AtlasSphereIdentityMolecule sphereId={selectedFactor.deactivatedBy} />
                ) : (
                  <span>{t('common.unknown')}</span>
                )}
              </span>
            ) : (
              <span>{t('statusFactors.deactivated.readOnly')}</span>
            )}
          </div>
        </div>
      )}

      {/* Validation errors - only show for active factors */}
      {!isDeactivated && hasValidationErrors && Object.keys(validationErrors).length > 0 && localData.factorType && (
        <div className="factor-detail-pane__errors">
          <Message
            severity="warn"
            text={t(
              'statusFactors.detail.hasErrors',
              'Please fix the errors below before applying changes.'
            )}
          />
        </div>
      )}

      {/* Content — summary or configure view */}
      <div className="factor-detail-pane__content">
        {viewMode === 'summary' && !isNew && !isDeactivated && sphereId ? (
          <FactorSummaryMolecule
            factor={{
              id: localData.factorId ?? selectedFactor.id,
              factorType: localData.factorType ?? 'manual',
              name: localData.name,
              description: localData.description,
              weight: localData.weight,
              // status carries the lifecycle overlay (observing/paused/deactivated
              // collapse onto the health value) — kept for callers that need
              // the sidebar-display view. currentStatus is the underlying
              // canonical health; the "why this color?" chip renders that so
              // observing/paused factors still show their real color, not
              // the lifecycle mode masquerading as a status.
              status: selectedFactor.status,
              statusLabel: selectedFactor.status,
              currentStatus: selectedFactor.currentStatus,
              velocityScore: selectedFactor.velocityScore,
              velocityBucket: selectedFactor.velocityBucket,
            }}
            sphereId={sphereId}
            hasEditPermission={hasEditPermission}
            mode={localData.mode}
            rollupSet={localData.rollupSet}
            readsAsContext={{ availableProperties, availablePerspectives }}
            onOpenConfigure={() => {
              logAnalyticsEvent('factor_configure_opened', undefined, {
                factorId: selectedFactor.id,
                factorType: localData.factorType,
                sphereId,
              });
              onSetViewMode?.('configure');
            }}
          />
        ) : (
          renderEditor()
        )}
      </div>

      {/* Footer — Reactivate for deactivated, normal actions (+ Resume when paused) for active */}
      {isDeactivated && hasEditPermission && onReactivate && (
        <div className="factor-detail-pane__footer">
          <div className="factor-detail-pane__footer-left" />
          <div className="factor-detail-pane__footer-right">
            <ButtonAtom
              type="button"
              label={t('statusFactors.actions.reactivate')}
              icon="pi pi-replay"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                handleReactivate();
              }}
              variant="primary"
              size="small"
              disabled={isReactivating}
              loading={isReactivating}
            />
          </div>
        </div>
      )}

      {!isDeactivated && hasEditPermission && localData.factorType && (isNew || viewMode === 'configure') && (
        <div className="factor-detail-pane__footer-region">
        {previewWanted && (
          <RollupChangePreview
            state={simulation.state}
            result={simulation.result}
            onRetry={simulation.retry}
            aggregationMethod={localData.aggregationMethod}
            weighting={localData.weighting}
            diagnosticContext={{
              reporting: simulation.result?.coverage.reporting,
              total: simulation.result?.coverage.total,
              minCoverage: localData.coverageFloor?.percent,
              floorStatusLabel: localData.coverageFloor
                ? t(`statusVocabulary.${localData.coverageFloor.status}`)
                : undefined,
              influenceFloor: localData.influenceFloor === null ? undefined : String(localData.influenceFloor),
              contributionUnavailableCount: simulation.result?.baseline.contributionUnavailableCount,
            }}
          />
        )}
        <div className="factor-detail-pane__footer">
          <div className="factor-detail-pane__footer-left">
            <span
              className="factor-detail-pane__unsaved-count"
              data-testid="factor-detail-pane-unsaved-count"
            >
              <i className="pi pi-info-circle" aria-hidden="true" />
              {unsavedCount > 0
                ? t('statusFactors.detail.unsavedChanges', { count: unsavedCount })
                : t('statusFactors.detail.noUnsavedChanges')}
            </span>
          </div>
          <div className="factor-detail-pane__footer-right">
            <ButtonAtom
              type="button"
              label={t('statusFactors.actions.revert', 'Revert')}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                onRevert();
              }}
              variant="secondary"
              size="small"
              disabled={!isDirty || isSaving}
            />
            <ButtonAtom
              type="button"
              label={t('statusFactors.actions.save', 'Save')}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                onApply();
              }}
              variant="primary"
              size="small"
              disabled={!isDirty || hasValidationErrors || isSaving}
              loading={isSaving}
            />
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

FactorDetailPaneMolecule.displayName = 'FactorDetailPaneMolecule';

export default FactorDetailPaneMolecule;
